/**
 * PtpUnifiedDashboard — PTP with Kept drawn inside it.
 *
 * Replaces the PTP tab and the Kept tab with one page. The two were always
 * describing one thing from two sides: Kept is a PART of PTP, and splitting
 * them across tabs meant the ratio between them — the number the page exists
 * to show — could only be read by remembering the other tab.
 *
 * The old pages are not deleted. PtpDashboard survives as "PTP Legacy" and
 * still carries the three breakdown tables (by DPD bucket, by DPD day, by
 * date) that this page does not; PaymentsDashboard is hidden but intact.
 *
 * ── EVERY MONEY AND COUNT FIGURE COMES FROM ONE SOURCE ──
 * /dashboard/payments-summary, which aggregates raw customerconvevent.
 *
 * That is a change from the old PTP page, which charted counts from
 * summary_ptp_hourly while its funnel read money from the payments route. The
 * two disagree — the summary table is built by a refresh proc and can lag the
 * events it was built from — so the funnel's PTP and the chart's PTP were two
 * numbers under one word. Here the funnel, the KPI row and all three stacked
 * charts read the same rows.
 *
 * The hourly summary is still fetched, for the three things it alone knows:
 * Sent, RPC and Uncommitted. RPC is a DENOMINATOR here and never a numerator,
 * so for PTP the mixing is confined to one rate. Uncommitted has no event in
 * the payments route at all — it exists only as future_ptp_* on
 * summary_ptp_hourly — so its section reads the hourly summary and nothing
 * else, and never feeds a PTP figure.
 *
 * ── WHAT PTP IS ──
 *   Kept         CF-AI3-PAY   payment taken today         payment_*
 *   Scheduled    CF-AI4-PTP   future-dated payment set    promise_*
 *   PTP          Kept + Scheduled
 *   Uncommitted  future_ptp_* on summary_ptp_hourly — NOT part of PTP
 *
 * An earlier version of this page labelled the CF-AI4-PTP half "Uncommitted"
 * and drew it in that section. That was Scheduled under the wrong name; the
 * real Uncommitted figure was not on the page at all.
 *
 * ── ⚠ "PTP COUNT" IS NOT total_ptp_count ──
 * The old chart was titled "Total PTP count" and plotted `total_ptp_count`,
 * which includes Uncommitted. Every other PTP on this page and the old ones —
 * the funnel step, the KPI row, the $ figures — means Kept + promises.
 *
 * Renaming the chart to "PTP count" without changing the series would have
 * left two different quantities sharing one word on one page, so the series
 * changed too. THE NUMBER IS LOWER THAN THE OLD CHART'S by the Uncommitted
 * count. Uncommitted has its own section further down.
 *
 * ── THE COLOURS ARE THE REPORT'S ──
 * rpt1's PDF is what gets forwarded, so it is the reference: RPC blue, PTP
 * green, Kept a darker shade of that green, with Accounts moved to brown to
 * free blue. Kept was purple, then dark amber, before this — see the palette
 * block below.
 *
 * ⚠ WHERE KEPT AND PTP ARE SIBLINGS RATHER THAN NESTED, THIS IS THE WRONG
 * ENCODING. Trends' outcome donut classifies each conversation as one or the
 * other, mutually exclusive, so two shades of amber there would be two
 * near-identical adjacent slices. That chart keeps a distinct hue and says so
 * at its definition.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, MenuItem, Select, Typography,
} from "@mui/material";
import {
  Area, AreaChart, CartesianGrid, ComposedChart, Legend, LabelList,
  ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  getDashboardCampaigns         as getCampaigns,
  getDashboardPaymentsSummary   as getPaymentsSummary,
  getDashboardPtpSummaryHourly  as getPtpSummary,
  getDashboardPtpByDayHourly    as getPtpByDay,
  getDashboardAccountsHourly    as getAccounts,
} from "../../services/pbi/pbiservices.js";
import {
  useClientTimeZone, zonedNow, zoneAbbr,
} from "../../utils/clienttimezone";

// ── Constants ──────────────────────────────────────────────────────
const BLUE    = "#378ADD";
const AMBER   = "#BA7517";
// Dotted average rule and the figure beside each chart title. A hue no series
// uses, so the annotation never reads as another measurement.
const AVG_INK = "#0F6E56";

// ── THE PALETTE IS THE REPORT'S ──
// rpt1's PDF is the artifact people forward, so it is the one that wins: RPC
// blue, PTP green, Kept a darker shade of that green. Accounts takes brown,
// which is what freed blue for RPC.
//
// A metric reads the same hue wherever it appears — funnel bar, KPI figure,
// line, area, table column — on every page and in the emailed PDF. Colour
// identifies rather than decorates.
//
// ── TWO GREENS FOR PTP, A THIRD FOR ITS NEIGHBOUR ──
//   PTP          mid green    Kept + Scheduled, the whole
//   Kept         dark green   the part that arrived (CF-AI3-PAY)
//   Uncommitted  mint         a separate measure, NOT part of PTP
// Kept is contained in PTP, so it is a shade of PTP's hue. Scheduled has no
// hue of its own — on the stacked charts it is the lighter band between Kept
// and the PTP total. Uncommitted keeps the mint it always had; it is a
// promise-type outcome so it stays in the green family, but it is not a band
// of PTP and is never stacked with it.
// Darken (pct < 0) or lighten (pct > 0) a hex colour. Same helper and same
// -0.42 the report uses, so the app and the PDF produce the identical pair.
const shade = (hex, pct) => {
  const n = parseInt(String(hex).slice(1), 16);
  const f = (c) => Math.max(0, Math.min(255, Math.round(c + c * pct)));
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255]
    .map(f).map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};
const KEPT_SHADE = -0.42;   // how much darker the Kept band sits

const C_SENT        = "#0C447C";  // navy
const C_ACCOUNTS    = "#8A5524";  // brown
const C_RPC         = "#378ADD";  // blue
const C_PTP         = "#1D9E75";  // green
const C_SL          = "#D4537E";  // pink
const C_UNCOMMITTED = "#3DBE92";  // mint — green family, but not a band of PTP

// Derived, not a literal, so it follows C_PTP wherever the palette goes. Kept
// is CONTAINED in PTP, and a shade of one hue says that where a second hue
// would say "different thing".
//
// ⚠ THIS LANDS AT #115C44, WHICH IS 26 RGB UNITS FROM THE BASELINE INK
// (#0F6E56). On a chart with a dotted average rule the two read as the same
// colour. The report has the same collision; see the note where AVG_INK is
// defined.
const C_KEPT        = shade(C_PTP, KEPT_SHADE);   // dark green

const GRAN_OPTIONS  = ["Day", "Week", "Month"];
const RANGE_OPTIONS = [
  "1D", "2D", "3D",
  "7D", "30D", "90D", "1Y",
  "C Wk", "CW+Last",
  "This Mo", "Cur+Last", "Prev 2Mo", "Prev 3Mo",
];

// ── Helpers ────────────────────────────────────────────────────────
function safeNum(v) { const n = Number(v); return isNaN(n) ? 0 : n; }

function fmtCurrency(n) {
  const num = Number(n);
  if (!num || isNaN(num)) return "$0";
  // Switch to M only once the K form would need 4 digits — i.e. once it
  // rounds to 1000.0K or more.
  if (Math.abs(Number((num / 1000).toFixed(1))) >= 1000)
    return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000) return `$${(num / 1000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}

// Graph currency — one decimal everywhere, so axis ticks and inline labels
// stay readable without the extra digit.
function fmtCurrencyG(n) {
  const num = Number(n);
  if (!num || isNaN(num)) return "$0.0";
  if (Math.abs(Number((num / 1000).toFixed(1))) >= 1000)
    return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000) return `$${(num / 1000).toFixed(1)}K`;
  return `$${num.toFixed(1)}`;
}

function fmtPct(v, dec = 2) {
  const n = Number(v);
  if (v == null || isNaN(n)) return "—";
  return `${n.toFixed(dec)}%`;
}

function fmtInt(v) { return safeNum(v).toLocaleString(); }

function fmtDate(val) {
  if (!val) return "";
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
}

function fmtAxisDate(val, gran = "day") {
  if (!val) return "";
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
  if (isNaN(d)) return String(val);
  if (String(gran).toLowerCase() === "month")
    return d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  const wd = ["Su","Mo","Tu","We","Th","Fr","Sa"][d.getDay()];
  const md = d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" });
  return `${wd} ${md}`;
}

// Still resolved client-side even though the API buckets by client zone: the
// range boundaries ("today", "this week") must be computed in the tenant's
// zone before being sent, or a range picked at 11pm PT resolves against the
// wrong "today".
function getDateRange(preset, timeZone) {
  const now = zonedNow(timeZone);
  const y   = now.getFullYear();
  const m   = now.getMonth();
  let start, end;

  const dow        = now.getDay();
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - ((dow + 6) % 7));
  thisMonday.setHours(0, 0, 0, 0);
  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);

  switch (preset) {
    case "1D":      start = new Date(now); start.setDate(now.getDate() - 1);   end = now; break;
    case "2D":      start = new Date(now); start.setDate(now.getDate() - 2);   end = now; break;
    case "3D":      start = new Date(now); start.setDate(now.getDate() - 3);   end = now; break;
    case "7D":      start = new Date(now); start.setDate(now.getDate() - 7);   end = now; break;
    case "30D":     start = new Date(now); start.setDate(now.getDate() - 30);  end = now; break;
    case "90D":     start = new Date(now); start.setDate(now.getDate() - 90);  end = now; break;
    case "1Y":      start = new Date(now); start.setDate(now.getDate() - 365); end = now; break;
    case "C Wk":    start = thisMonday; end = now; break;
    case "CW+Last": start = lastMonday; end = now; break;
    case "This Mo": start = new Date(y, m, 1);     end = now; break;
    case "Cur+Last":start = new Date(y, m - 1, 1); end = now; break;
    case "Prev 2Mo":start = new Date(y, m - 2, 1); end = new Date(y, m, 0); break;
    case "Prev 3Mo":
    default:        start = new Date(y, m - 3, 1); end = new Date(y, m, 0); break;
  }

  // Serialised from the date parts — toISOString() converts to UTC first,
  // which shifts the date by a day in negative-offset zones.
  const toLocal = (d) => {
    const yy = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${yy}-${mo}-${dy}`;
  };
  return { startDate: toLocal(start), endDate: toLocal(end) };
}

// ── AVERAGE RULES ──────────────────────────────────────────────────
// The two the scorecard uses, for the same reasons.
//
// activeMean — mean over buckets WITH ACTIVITY only. A day the system was not
// running is not a zero; counting it drags the rule down and makes every real
// day look above trend.
//
// pooledPct — for a RATE, total numerator over total denominator. NOT the mean
// of the per-bucket percentages, which weights a 40-send day the same as a
// 4,000-send one; on a book whose volume moves the two differ by multiples.
function activeMean(rows, key) {
  const active = (rows || []).filter(r => safeNum(r[key]) > 0);
  return active.length
    ? active.reduce((a, r) => a + safeNum(r[key]), 0) / active.length
    : 0;
}

function pooledPct(rows, numKey, denKey) {
  const den = (rows || []).reduce((a, r) => a + safeNum(r[denKey]), 0);
  return den
    ? ((rows || []).reduce((a, r) => a + safeNum(r[numKey]), 0) * 100) / den
    : 0;
}

// Funnel totals from the payments rows, with a flag saying whether there were
// any. The funnel falls back to the hourly summary when there were none, so a
// failed payments fetch degrades to the previous numbers rather than an empty
// funnel.
function paymentsTotals(rows) {
  const t = (rows || []).reduce((a, r) => ({
    kept:       a.kept       + safeNum(r.payment_count),
    keptAmt:    a.keptAmt    + safeNum(r.payment_total),
    promise:    a.promise    + safeNum(r.promise_count),
    promiseAmt: a.promiseAmt + safeNum(r.promise_total),
    sl:         a.sl         + safeNum(r.smartlink_count),
    slAmt:      a.slAmt      + safeNum(r.smartlink_total),
  }), { kept: 0, keptAmt: 0, promise: 0, promiseAmt: 0, sl: 0, slAmt: 0 });
  return {
    ...t,
    ptp:    t.kept + t.promise,
    ptpAmt: t.keptAmt + t.promiseAmt,
    has:    (rows || []).length > 0,
  };
}

// ── Small components ───────────────────────────────────────────────
// Same section header as the Texts tab (TrendsDashboard), so the label above
// each tab's KPI row looks identical. Used once here — above the KPI row. The
// chart sections below carry their own card titles and take no header.
function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

// ── LANGUAGE SPLIT ────────────────────────────────────────────────
// Per-language figures arrive from the API with an _en / _sp / _ot suffix,
// resolved from each campaign's [Language] tag server-side. "Other" is
// anything untagged and is drawn only when non-zero, so on a fully tagged book
// EN and SP account for the whole tile.
const LANGS = [
  { key: "en", label: "EN" },
  { key: "sp", label: "SP" },
  { key: "ot", label: "Other", hideIfZero: true },
];

// Language rows for a tile whose % is SHARE of the tile's total.
//   get(langKey) → { count, amount? }
function shareRows(total, get) {
  return LANGS.map((l) => {
    const v = get(l.key) || {};
    const count = safeNum(v.count);
    return {
      ...l, count,
      amount: v.amount == null ? null : safeNum(v.amount),
      pct: total > 0 ? (count / total) * 100 : null,
    };
  }).filter((r) => !(r.hideIfZero && r.count === 0 && !r.amount));
}

/**
 * KPI tile with the language split inside it. Gray background, matching the
 * other KPI cards; the EN / SP lines sit under the headline figure.
 *
 *   TEXTS SENT
 *   18,240
 *   ▲ +4.1% vs 17,520
 *   ────────────────────
 *   EN  81.4%    14,850
 *   SP  18.6%     3,390
 *
 * A row with an `amount` adds a $ column; `pct` is whatever the caller says it
 * is (share of total on most tiles, the language's own rate on a rate tile).
 * `fmtMoney` is passed in because each page formats dollars its own way.
 */
function LangKpiTile({ label, value, aside, color, delta, rows, fmtMoney }) {
  const hasAmount = rows.some((r) => r.amount != null);
  const cols = hasAmount ? "30px 48px 1fr auto" : "30px 48px 1fr";
  return (
    <Box sx={{
      background: "var(--mui-palette-background-default, #f7f7f7)",
      borderRadius: 2, p: "10px 14px", minWidth: 0,
      display: "flex", flexDirection: "column",
    }}>
      <Typography sx={{
        fontSize: 11, color: "text.secondary", mb: 0.5,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2, whiteSpace: "nowrap" }}>
          {value}
        </Typography>
        {aside != null && (
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "text.primary", whiteSpace: "nowrap" }}>
            {aside}
          </Typography>
        )}
      </Box>
      {delta && (
        <Typography sx={{ fontSize: 11, color: delta.good ? "#3B6D11" : "#A32D2D", mt: 0.5 }}>
          {delta.up ? "▲" : "▼"} {delta.text}
        </Typography>
      )}
      <Box sx={{ borderTop: "0.5px solid", borderColor: "divider", mt: 0.75, pt: 0.5 }}>
        {rows.map((r) => {
          const muted = r.key === "ot";
          return (
            <Box key={r.key} sx={{
              display: "grid", gridTemplateColumns: cols,
              alignItems: "center", columnGap: 0.75, fontSize: 11.5, py: "1px",
              color: muted ? "text.disabled" : "text.secondary",
            }}>
              <Box sx={{ fontWeight: 600 }}>{r.label}</Box>
              <Box sx={{ fontWeight: 600, color: muted ? "inherit" : color }}>
                {r.pct == null ? "—" : `${r.pct.toFixed(1)}%`}
              </Box>
              <Box sx={{ textAlign: "right" }}>{safeNum(r.count).toLocaleString()}</Box>
              {hasAmount && (
                <Box sx={{ textAlign: "right", minWidth: 52 }}>
                  {r.amount == null ? "" : fmtMoney(r.amount)}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// Per-language { count, amount } from the payments rows, summed over one or
// more prefixes. PTP passes ["payment", "promise"]; Same Day ["payment"];
// Smart Links ["smartlink"].
function langTotals(rows, prefixes) {
  const out = {};
  LANGS.forEach((l) => {
    let count = 0, amount = 0;
    (rows || []).forEach((r) => prefixes.forEach((p) => {
      count  += safeNum(r[`${p}_${l.key}_count`]);
      amount += safeNum(r[`${p}_${l.key}_total`]);
    }));
    out[l.key] = { count, amount };
  });
  return out;
}

function ChartCard({ title, right, children, height = 220 }) {
  return (
    <Box sx={{
      border: "0.5px solid", borderColor: "divider",
      borderRadius: 2, p: "12px 14px",
    }}>
      {(title || right) && (
        <Box sx={{ display: "flex", alignItems: "baseline",
                   justifyContent: "space-between", gap: 1, mb: 1 }}>
          <Typography sx={{
            fontSize: 11, fontWeight: 500, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em",
          }}>
            {title}
          </Typography>
          {right && (
            <Typography sx={{ fontSize: 11, color: AVG_INK, whiteSpace: "nowrap" }}>
              {right}
            </Typography>
          )}
        </Box>
      )}
      <Box sx={{ width: "100%", height: height === "auto" ? "auto" : height }}>
        {children}
      </Box>
    </Box>
  );
}

function PillGroup({ label, options, value, onChange, groups }) {
  let groupBoundaries = new Set();
  if (groups) {
    let idx = 0;
    groups.forEach((size, gi) => {
      if (gi < groups.length - 1) { idx += size; groupBoundaries.add(idx); }
    });
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt, i) => {
          const active = value === opt;
          return (
            <React.Fragment key={opt}>
              {groupBoundaries.has(i) && (
                <Box sx={{ width: "1px", height: 18, bgcolor: "divider", mx: 0.5, flexShrink: 0 }} />
              )}
              <Box onClick={() => onChange(opt)} sx={{
                px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
                cursor: "pointer", userSelect: "none", border: "0.5px solid",
                borderColor: active ? BLUE : "divider",
                background:  active ? BLUE : "transparent",
                color:       active ? "#fff" : "text.secondary",
                fontWeight:  active ? 500 : 400,
                transition: "all .15s",
                "&:hover": { borderColor: BLUE },
              }}>{opt}</Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Funnel ─────────────────────────────────────────────────────────
//
// Sent → Accounts → RPC → PTP → Kept → Smart Links, unchanged from the two
// pages this replaces.
//
// ── THE BAR'S LENGTH IS THE NUMBER PRINTED IN IT ──
// Each step past the first carries its own denominator: RPC against Accounts,
// PTP and Smart Links against RPC, Kept against PTP. The fill equals the
// percentage shown inside the bar, 1:1, so the bar and the number agree. The
// share of Sent drops to the small line under the label.
//
// Kept against PTP is the same ratio the stacked charts below draw as a band
// inside a curve — the funnel states it once for the window, the charts show
// how it moved.
function Funnel({ summary, accounts = 0, pay }) {
  const sent = safeNum(summary.texts_sent);
  // RPC is GROSS of opt-outs — rpc_count from the route is net, so they are
  // added back. A STOP is still a reply.
  const rpc  = safeNum(summary.rpc_count) + safeNum(summary.optout_count);

  const kept      = pay?.has ? pay.kept   : safeNum(summary.processed_ptp_count);
  const ptp       = pay?.has ? pay.ptp
                             : safeNum(summary.processed_ptp_count) + safeNum(summary.promise_count);
  const sl        = pay?.has ? pay.sl     : safeNum(summary.smartlink_count);
  const keptAmt   = pay?.has ? pay.keptAmt : safeNum(summary.processed_ptp_amount);
  const ptpAmount = pay?.has ? pay.ptpAmt
                             : safeNum(summary.processed_ptp_amount) + safeNum(summary.promise_amount);
  const slAmount  = pay?.has ? pay.slAmt  : safeNum(summary.smartlink_amount);

  const base = sent || 1;
  const steps = [
    { label: "Sent",        val: sent,     color: C_SENT },
    { label: "Accounts",    val: accounts, color: C_ACCOUNTS },
    { label: "RPC",         val: rpc,      color: C_RPC,  den: accounts },
    { label: "PTP",         val: ptp,      color: C_PTP,  den: rpc, amount: ptpAmount },
    { label: "Same Day",        val: kept,     color: C_KEPT, den: ptp, amount: keptAmt },
    { label: "Smart Links", val: sl,       color: C_SL,   den: rpc, amount: slAmount },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, py: 0.5 }}>
      {steps.map((s, i) => {
        const pctOfSent = base > 0 ? (s.val / base) * 100 : 0;
        const denPct    = s.den > 0 ? (s.val / s.den) * 100 : null;
        const isFirst   = i === 0;
        // No magnification. Floored at 2% so a near-zero step still shows a
        // sliver of colour rather than reading as a missing stage.
        const shownPct  = isFirst ? 100 : (denPct != null ? denPct : pctOfSent);
        const fillW     = shownPct > 0 ? Math.min(100, Math.max(shownPct, 2)) : 0;
        return (
          <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{
                position: "relative", width: "100%", minWidth: 0,
                height: 46, background: "#f0f0f0", borderRadius: "4px",
              }}>
                <Box sx={{
                  position: "absolute", left: 0, top: 0, bottom: 0,
                  width: `${fillW}%`, background: s.color,
                  borderRadius: isFirst ? "4px" : "4px 0 0 4px",
                }} />
                <Box sx={{
                  position: "absolute", inset: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, fontWeight: 600, color: isFirst ? "#fff" : "#333",
                  pointerEvents: "none", whiteSpace: "nowrap",
                }}>
                  {isFirst ? fmtInt(s.val)
                           : denPct != null ? `${denPct.toFixed(1)}%`
                                            : `${pctOfSent.toFixed(1)}%`}
                </Box>
              </Box>
            </Box>

            <Box sx={{
              width: 90, flexShrink: 0, textAlign: "left",
              fontSize: 12, color: "text.secondary", lineHeight: 1.35,
            }}>
              <Box sx={{ color: s.color, fontWeight: 600 }}>{s.label}</Box>
              <Box sx={{ mt: 0.25, whiteSpace: "nowrap" }}>
                {!isFirst && <span style={{ fontWeight: 500 }}>{fmtInt(s.val)}</span>}
                {!isFirst && s.amount != null && (
                  <span> · <b style={{ color: s.color }}>{fmtCurrency(s.amount)}</b></span>
                )}
              </Box>
              {denPct != null && (
                <Box sx={{ mt: 0.25, fontSize: 10, color: "text.disabled", whiteSpace: "nowrap" }}>
                  {pctOfSent.toFixed(1)}% of Sent
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ── Stacked area: Kept inside PTP ──────────────────────────────────
/**
 * Two bands, one quantity. The full height is the PTP figure the title names;
 * the darker band at the axis is the Kept part of it, and the gap between them
 * is the shortfall — the same ratio the funnel's Kept step prints, readable
 * without arithmetic.
 *
 * ── THE VALUES ARE NOT ADDED ──
 * `rest` is total MINUS kept, computed by the caller, because Kept is inside
 * PTP by construction and stacking the two raw figures would draw the money
 * twice. The top label prints the TOTAL rather than `rest`, so the number at
 * the top of the stack is the one the title promises.
 *
 * ── KEPT ON THE BOTTOM ──
 * The eye starts at the axis, and the solid base is the money actually in
 * hand. The lighter band above it is what was promised and has not landed.
 *
 * ── CLAMPED AT ZERO ──
 * Kept can in principle exceed the PTP it is measured against — they are
 * different event sets over a fixed window, not a strict subset, so a payment
 * landing against a promise made before the range opens has no matching PTP
 * inside it. A negative segment would render below the axis; clamped, the day
 * reads as all-Kept and the printed total is still the PTP figure.
 *
 * ⚠ Gradient ids are document-global in SVG, so two charts sharing one both
 * take whichever rendered last. `id` is per chart for that reason.
 */
function StackedPtpChart({
  id, title, right, data, fmtVal, tickFmt, baseline, height = 220,
}) {
  const gradId = `grad_${id}`;

  if (!data.length) {
    return (
      <ChartCard title={title} height={height}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center",
                   height: "100%", fontSize: 12, color: "text.disabled" }}>
          No data for this period
        </Box>
      </ChartCard>
    );
  }

  return (
    <ChartCard title={title} right={right} height={height}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={C_PTP} stopOpacity={0.15} />
              <stop offset="95%" stopColor={C_PTP} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#888" }} />
          <YAxis tick={{ fontSize: 10, fill: "#888" }} tickFormatter={tickFmt} />
          <Tooltip
            contentStyle={{ fontSize: 12 }}
            // `rest` is a drawing artefact, not a figure anyone asked for, so
            // the tooltip reports Kept and the total instead of Kept and the
            // remainder.
            formatter={(v, n, p) =>
              n === "PTP" ? [fmtVal(p?.payload?.total), "PTP"] : [fmtVal(v), n]}
          />
          {baseline != null && (
            <ReferenceLine y={baseline} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
          )}

          {/* Same Day (CF-AI3-PAY): bottom of the stack, its own hue, solid
              enough to read as the base rather than as a wash. Shown as
              "Same Day" in the legend and tooltip to match the funnel step;
              the data key stays `kept`. */}
          <Area type="monotone" dataKey="kept" name="Same Day" stackId="ptp"
                stroke={C_KEPT} fill={C_KEPT} fillOpacity={0.55}
                strokeWidth={2} dot={{ r: 2.5 }}>
            <LabelList dataKey="kept" position="insideTop" offset={6}
                       fontSize={9} fill="#fff"
                       formatter={v => (safeNum(v) > 0 ? fmtVal(v) : "")} />
          </Area>

          {/* The remainder, carrying the TOTAL as its label. */}
          <Area type="monotone" dataKey="rest" name="PTP" stackId="ptp"
                stroke={C_PTP} fill={`url(#${gradId})`}
                strokeWidth={2} dot={{ r: 2.5 }}>
            <LabelList dataKey="total" position="top" offset={8}
                       fontSize={10} fill={C_PTP} formatter={fmtVal} />
          </Area>

          <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// Single-series area — Uncommitted and Smart Links, which have nothing nested
// inside them.
function PlainAreaChart({ id, title, right, data, dataKey, name, color,
                          fmtVal, tickFmt, baseline, height = 220 }) {
  const gradId = `grad_${id}`;
  return (
    <ChartCard title={title} right={right} height={height}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 18, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={color} stopOpacity={0.15} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#888" }} />
          <YAxis tick={{ fontSize: 10, fill: "#888" }} tickFormatter={tickFmt} />
          <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v, n) => [fmtVal(v), n]} />
          {baseline != null && (
            <ReferenceLine y={baseline} stroke={AVG_INK} strokeDasharray="3 3" strokeWidth={1} />
          )}
          <Area type="monotone" dataKey={dataKey} name={name}
                stroke={color} fill={`url(#${gradId})`} strokeWidth={2} dot={{ r: 3 }}>
            <LabelList dataKey={dataKey} position="top" offset={8}
                       fontSize={10} fill={color} formatter={fmtVal} />
          </Area>
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// ── Main ───────────────────────────────────────────────────────────
export default function PtpUnifiedDashboard({ channel }) {
  // Day / This Mo on open: a daily series over the current month is the view
  // people land on this page for. Week/90D smoothed away the movement the page
  // exists to show, and had every visit start with two clicks.
  //
  // "This Mo" rather than the rolling "30D" it replaced, so the page agrees
  // with the MTD tab and the emailed report about which days it is describing.
  // A rolling window quietly answers a different question every morning —
  // "the last 30 days" on the 9th reaches back into the previous month, and a
  // figure read off it cannot be checked against anything month-shaped.
  //
  // ⚠ IT IS SHORT AT THE START OF A MONTH. On the 3rd the series is three
  // points; on the 1st it is one. That is honest — there genuinely is only
  // one day of this month — but it is a thinner page than 30D ever gave, and
  // the fixed-window pills are one click away when someone wants depth.
  const [gran,        setGran]        = useState("Day");
  const [rangePreset, setRangePreset] = useState("This Mo");
  const [campaign,    setCampaign]    = useState("All");
  const [campaigns,   setCampaigns]   = useState(["All"]);

  const [summary,    setSummary]    = useState({});   // hourly PTP summary — Sent/RPC only
  const [byDayRaw,   setByDayRaw]   = useState([]);   // hourly, per (date, dpd) — RPC per day
  const [paySummary, setPaySummary] = useState([]);   // payments route — every money figure
  const [accounts,   setAccounts]   = useState(0);

  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  const { timeZone, tzReady } = useClientTimeZone();
  const channelParam = (channel || "texts").toLowerCase();
  const isEmails     = channelParam === "emails";

  useEffect(() => {
    getCampaigns()
      .then(data => setCampaigns(["All", ...(data || []).map(c => c.name)]))
      .catch(() => {});
  }, []);

  const params = useMemo(() => ({
    ...getDateRange(rangePreset, timeZone),
    campaign:    campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
    channel:     channelParam,
  }), [rangePreset, campaign, gran, channelParam, timeZone]);

  // ── NO PREVIOUS-PERIOD FETCH ──
  // The two pages this replaces each pulled a second full window to feed KPI
  // deltas. No KPI here carries a delta — each is a total with its EN / SP split — so
  // that window would be fetched and thrown away. If deltas come back, so does
  // the fetch; PtpDashboard still has the buildDelta helper to copy.
  const loadAll = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [s, day, pay, acc] = await Promise.all([
        getPtpSummary(params),
        getPtpByDay(params),
        getPaymentsSummary(params).catch(() => []),
        getAccounts(params).catch(() => ({})),
      ]);
      setSummary(s      || {});
      setByDayRaw(day   || []);
      setPaySummary(pay || []);
      setAccounts(safeNum(acc?.account_count));
    } catch (e) {
      console.error("PTP load error:", e);
      setError("Failed to load PTP data");
    } finally {
      setLoading(false);
    }
  }, [params]);

  // Gated on tzReady — firing before the client config lands would request a
  // UTC-derived window and then immediately refetch with the real zone.
  useEffect(() => { if (tzReady) loadAll(); }, [loadAll, tzReady]);

  // ── ONE DAILY SERIES, TWO SOURCES, CLEARLY DIVIDED ──
  //
  // PTP counts and money come from the payments rows. RPC comes from the
  // hourly summary and is used ONLY as the denominator of the rate chart — it
  // is never a PTP numerator, so the one place the two sources meet is a
  // single ratio rather than every figure on the page.
  //
  // Uncommitted also comes from the hourly summary (future_ptp_*), because
  // that is the only place it exists. It is carried on the same rows for the
  // Uncommitted chart and never added into a PTP figure.
  //
  // Keyed on summary_date, which both routes bucket in the tenant zone at the
  // same granularity, so the two halves land on the same rows. A date present
  // in one and not the other still gets a row, with zeros for the missing
  // half — which is the honest rendering of "we sent that day and nothing
  // converted", or vice versa.
  const daily = useMemo(() => {
    const byDate = new Map();
    const touch = (d) => {
      if (!byDate.has(d)) {
        byDate.set(d, {
          key: d, rpc: 0, sent: 0,
          keptCount: 0, ptpCount: 0,
          keptAmt: 0, ptpAmt: 0, slAmt: 0,
          uncCount: 0, uncAmt: 0,
        });
      }
      return byDate.get(d);
    };

    // byDayRaw is one row per (date, dpd bucket), so the day is the sum.
    (byDayRaw || []).forEach(r => {
      const a = touch(r.summary_date);
      a.sent     += safeNum(r.texts_sent);
      a.rpc      += safeNum(r.rpc_count) + safeNum(r.optout_count);
      a.uncCount += safeNum(r.future_ptp_count);
      a.uncAmt   += safeNum(r.future_ptp_amount);
    });

    // PTP = CF-AI3-PAY (payment_*) + CF-AI4-PTP (promise_*).
    (paySummary || []).forEach(r => {
      const a = touch(r.summary_date);
      a.keptCount += safeNum(r.payment_count);
      a.ptpCount  += safeNum(r.payment_count) + safeNum(r.promise_count);
      a.keptAmt   += safeNum(r.payment_total);
      a.ptpAmt    += safeNum(r.payment_total) + safeNum(r.promise_total);
      a.slAmt     += safeNum(r.smartlink_total);
    });

    return [...byDate.values()]
      .sort((a, b) => String(a.key).localeCompare(String(b.key)))
      .map(r => ({
        ...r,
        date:       fmtAxisDate(r.key, gran),
        ptpPct:     r.rpc ? (r.ptpCount  / r.rpc) * 100 : 0,
        keptPctRpc: r.rpc ? (r.keptCount / r.rpc) * 100 : 0,
      }));
  }, [byDayRaw, paySummary, gran]);

  // The three stacked series. `rest` is the drawn remainder; `total` is what
  // the top label prints. Clamped at zero — see the note on StackedPtpChart.
  const stack = (rows, totalKey, keptKey) => rows.map(r => ({
    date:  r.date,
    kept:  safeNum(r[keptKey]),
    total: safeNum(r[totalKey]),
    rest:  Math.max(0, safeNum(r[totalKey]) - safeNum(r[keptKey])),
  }));

  const pctSeries   = useMemo(() => stack(daily, "ptpPct",   "keptPctRpc"), [daily]);
  const amtSeries   = useMemo(() => stack(daily, "ptpAmt",   "keptAmt"),    [daily]);
  const countSeries = useMemo(() => stack(daily, "ptpCount", "keptCount"),  [daily]);

  // Rates pooled, totals the mean of the buckets that had activity. One rule
  // per chart, on the TOTAL — the level the chart is about. The Kept average
  // is the funnel's Kept step.
  const avgPtpPct   = pooledPct(daily, "ptpCount", "rpc");
  const avgPtpAmt   = activeMean(daily, "ptpAmt");
  const avgPtpCount = activeMean(daily, "ptpCount");
  const avgUncAmt   = activeMean(daily, "uncAmt");
  const avgSlAmt    = activeMean(daily, "slAmt");

  const payTotals = useMemo(() => paymentsTotals(paySummary), [paySummary]);

  // ── KPI ROW ──
  // PTP, Same Day and Smart Links read the same payments rows as the funnel,
  // so each tile's headline equals the funnel step. Uncommitted reads the
  // hourly summary (future_ptp_*), the only place it exists. Every tile's
  // EN / SP lines come off the same source as its headline, so they sum to it.
  //
  // % on every line is that language's SHARE OF THE COUNT in the tile; the $
  // beside it is that language's own total.
  const ptpLang  = useMemo(() => langTotals(paySummary, ["payment", "promise"]), [paySummary]);
  const sdLang   = useMemo(() => langTotals(paySummary, ["payment"]),            [paySummary]);
  const slLang   = useMemo(() => langTotals(paySummary, ["smartlink"]),          [paySummary]);
  const uncCount = safeNum(summary.future_ptp_count);
  const uncAmt   = safeNum(summary.future_ptp_amount);

  const ptpRows = shareRows(payTotals.ptp,  k => ptpLang[k]);
  const sdRows  = shareRows(payTotals.kept, k => sdLang[k]);
  const slRows  = shareRows(payTotals.sl,   k => slLang[k]);
  const uncRows = shareRows(uncCount, k => ({
    count:  summary[`future_ptp_count_${k}`],
    amount: summary[`future_ptp_amount_${k}`],
  }));

  const tzLabel = zoneAbbr(timeZone);

  // ── THE RANGE THE PILLS RESOLVED TO ──
  // Appended to every chart title, the way the scorecard does it. The pills
  // say "90D"; only this says which 90 days, and a screenshot or a PDF export
  // carries no pill state at all.
  const rangeLabel = `${fmtDate(params.startDate)} – ${fmtDate(params.endDate)}`;
  const titleOf    = (t) => (rangeLabel ? `${t} · ${rangeLabel}` : t);

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Slicers ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <PillGroup label="Granularity" options={GRAN_OPTIONS} value={gran} onChange={setGran} />
        <PillGroup label="Date range" options={RANGE_OPTIONS}
                   value={rangePreset} onChange={setRangePreset} groups={[3, 4, 2, 4]} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignSelf: "flex-end" }}>
          <Typography sx={{ fontSize: 11, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Campaign
          </Typography>
          <Select value={campaign} onChange={e => setCampaign(e.target.value)}
            size="small" variant="outlined"
            sx={{
              fontSize: 12, borderRadius: "20px", minWidth: 120, height: 28,
              ".MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
              ".MuiSelect-select": { py: "3px", px: "10px", fontSize: 12, color: "text.secondary" },
              ".MuiSvgIcon-root": { fontSize: 16, color: "text.secondary" },
            }}>
            {campaigns.map(c => (
              <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ ml: "auto", alignSelf: "flex-end", fontSize: 11, color: "text.secondary" }}>
          Times shown in {tzLabel}
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading PTP data…
        </Box>
      )}

      {/* Payment and promise events only exist on the texts channel, and every
          figure on this page derives from them. Same notice the Kept page
          carried, for the same reason. */}
      {!loading && isEmails && (
        <Box sx={{
          mt: 2, p: 3, borderRadius: 2,
          border: "0.5px solid", borderColor: "divider",
          color: "text.secondary", fontSize: 13,
        }}>
          Payment and promise data is only available for the Texts channel.
        </Box>
      )}

      {!loading && !isEmails && (
        <>
          {/* ── ONE ROW, FOUR TILES ──
              Count leads, $ beside it, then EN / SP by share of count, count
              and $. Replaces the Total / Avg / Max / Min rows that sat above
              each section: the total is the headline here, and the funnel and
              charts already carry the rates and the movement. */}
          <SectionTitle>Promise to Pay (PTP)</SectionTitle>
          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0,1fr))", md: "repeat(4, minmax(0,1fr))" },
            gap: 1, mb: 1.5,
          }}>
            <LangKpiTile label="PTP" color={C_PTP}
              value={fmtInt(payTotals.ptp)} aside={fmtCurrency(payTotals.ptpAmt)}
              rows={ptpRows} fmtMoney={fmtCurrency} />
            <LangKpiTile label="Same Day" color={C_KEPT}
              value={fmtInt(payTotals.kept)} aside={fmtCurrency(payTotals.keptAmt)}
              rows={sdRows} fmtMoney={fmtCurrency} />
            <LangKpiTile label="Uncommitted" color={C_UNCOMMITTED}
              value={fmtInt(uncCount)} aside={fmtCurrency(uncAmt)}
              rows={uncRows} fmtMoney={fmtCurrency} />
            <LangKpiTile label="Smart Links" color={C_SL}
              value={fmtInt(payTotals.sl)} aside={fmtCurrency(payTotals.slAmt)}
              rows={slRows} fmtMoney={fmtCurrency} />
          </Box>

          {/* No section header here: the chart card below is already titled
              "PTP $", and a header repeating it read as a duplicate. */}

          {/* The MONEY chart and the funnel share the top row; the two below
              run full width. The funnel is the window as a whole — one
              snapshot of the flow — and the charts carry how it moved.

              PTP $ leads because it is the KPI row's first tile made into a
              series: the tile gives the window's total, the chart gives the
              same money over time with Same Day inside it. The rate follows, once the amounts it
              is a rate ON have been seen. */}
          <Box sx={{ display: "grid", gridTemplateColumns: "3fr 1fr", gap: 1.5, mb: 1.5 }}>
            <StackedPtpChart
              id="ptp_amount"
              title={titleOf("PTP $")}
              right={`avg ${fmtCurrencyG(avgPtpAmt)}`}
              data={amtSeries}
              fmtVal={fmtCurrencyG}
              tickFmt={fmtCurrencyG}
              baseline={avgPtpAmt}
              height={340}
            />
            <ChartCard title={titleOf("Conversion funnel")} height="auto">
              <Funnel summary={summary} accounts={accounts} pay={payTotals} />
            </ChartCard>
          </Box>

          <Box sx={{
            mb: 1.5, p: "10px 14px", borderRadius: 2,
            border: "0.5px solid", borderColor: `${AMBER}55`,
            background: `${AMBER}0F`, fontSize: 12, color: "text.secondary", lineHeight: 1.5,
          }}>
            <b style={{ color: AMBER }}>Note:</b>{" "}
            <b>RPC</b> is the count of customers who responded to the texts, including
            opt-outs — a STOP is still a reply, and it is counted here as one.{" "}
            <b>Same Day</b> are immediate payments processed through PNM (CF-AI3-PAY),{" "}
            <b>Scheduled</b> are future-dated payments set up through PNM (CF-AI4-PTP), and{" "}
            <b>PTP</b> is the two together — the base Same Day is measured against. On the
            three charts above and below, the darker band at the axis is Same Day and the full
            height is PTP, so the lighter band between them is Scheduled.{" "}
            <b>EN</b> / <b>SP</b> in the KPI tiles come from each campaign's language tag;
            the % is that language's share of the tile's count, and an <b>Other</b> line
            appears only for an untagged campaign.{" "}
            <b>Uncommitted</b> is a separate measure and is not part of PTP. PTP figures come
            from the payment events, bucketed in <b>{tzLabel}</b>; <b>Sent</b>, <b>RPC</b>{" "}
            and <b>Uncommitted</b> come from the hourly summary, and RPC appears only as the
            denominator of the rate above.
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <StackedPtpChart
              id="ptp_pct_of_rpc"
              title={titleOf("PTP % of RPC")}
              right={`avg ${fmtPct(avgPtpPct, 1)}`}
              data={pctSeries}
              fmtVal={v => fmtPct(v, 1)}
              tickFmt={v => `${Math.round(v)}%`}
              baseline={avgPtpPct}
            />
          </Box>

          {/* ⚠ PTP count is Kept + promises — NOT total_ptp_count, which the
              old "Total PTP count" chart plotted and which folds Uncommitted
              in. See the note at the top of this file: the number is lower
              than the old chart's, deliberately. */}
          <Box sx={{ mb: 1.5 }}>
            <StackedPtpChart
              id="ptp_count"
              title={titleOf("PTP count")}
              right={`avg ${Math.round(avgPtpCount).toLocaleString()}`}
              data={countSeries}
              fmtVal={fmtInt}
              tickFmt={fmtInt}
              baseline={avgPtpCount}
            />
          </Box>

          {/* ── UNCOMMITTED ──
              NOT part of PTP. future_ptp_* from summary_ptp_hourly — the only
              place it is recorded. Before this it showed promise_total, which
              is CF-AI4-PTP: the Scheduled half of PTP under the wrong name.

              Its totals and EN / SP split are in the KPI row at the top. No
              section header: the chart card is titled "Uncommitted $". */}
          <Box sx={{ mb: 1.5 }}>
            <PlainAreaChart
              id="unc_amount"
              title={titleOf("Uncommitted $")}
              right={`avg ${fmtCurrencyG(avgUncAmt)}`}
              data={daily} dataKey="uncAmt" name="Uncommitted amount"
              color={C_UNCOMMITTED}
              fmtVal={fmtCurrencyG} tickFmt={fmtCurrencyG}
              baseline={avgUncAmt}
            />
          </Box>

          {/* ── SMART LINKS ──
              Not a component of PTP — a smart link is its own event code, so it
              is not nested in anything and reads against RPC in the funnel. No
              section header: the chart card is titled "Smart Links $". */}
          <Box sx={{ mb: 1.5 }}>
            <PlainAreaChart
              id="sl_amount"
              title={titleOf("Smart Links $")}
              right={`avg ${fmtCurrencyG(avgSlAmt)}`}
              data={daily} dataKey="slAmt" name="Offer amount"
              color={C_SL}
              fmtVal={fmtCurrencyG} tickFmt={fmtCurrencyG}
              baseline={avgSlAmt}
            />
          </Box>
        </>
      )}
    </Box>
  );
}